import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {DraggableSwitcherItem, DraggableSwitcherItemPublicProps} from './draggableSwitcherItem';

/*
 * Props.
 */

interface EntryPointSwitcherItemProps extends DraggableSwitcherItemPublicProps {
  name: string;
  color: PaletteColorsEnum;
  iconBaseUrl?: string;
  iconUrl?: string;
}

/*
 * Style.
 */

interface ColorWrapperStyleProps {
  $isDragging: boolean;
  $isSelected?: boolean;
  $color: PaletteColorsEnum;
}

const StyledColorWrapperDiv = styled.div<ColorWrapperStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};
  color: ${(p) => p.theme.palette[p.$color].shade40};
`;

const StyledInitialDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  text-transform: uppercase;
  margin-bottom: 1px;
`;
interface IconStyleProps {
  $isSelected: boolean;
}

const StyledIconDiv = styled.div<IconStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};
`;

const StyledDefaultIconDiv = styled(StyledIconDiv)`
  ${(p) => addDefaultStyle(p.$isSelected)}
`;

function addDefaultStyle(isSelected: boolean) {
  // Hide on selected.
  if (isSelected) {
    return css`
      display: none;
    `;
  }

  // Hide on hover.
  return css`
    *:hover > && {
      display: none;
    }
  `;
}

const StyledHoverIconDiv = styled(StyledIconDiv)`
  ${(p) => addHoverStyle(p.$isSelected)}
`;

function addHoverStyle(isSelected: boolean) {
  // Hide on selected.
  if (isSelected) {
    return css`
      display: none;
    `;
  }

  // Show on hover.
  return css`
    display: none;
    *:hover > && {
      display: flex;
    }
  `;
}

const StyledSelectedIconDiv = styled(StyledIconDiv)`
  /* Hide on non-selected. */
  ${(p) => !p.$isSelected && 'display: none;'}
`;

interface StyledImageWrapperDivProps {
  $isSelected: boolean;
}

const StyledImageWrapperDiv = styled.div<StyledImageWrapperDivProps>`
  display: flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
`;

const StyledImg = styled.img`
  width: 24px;
  height: 24px;
  margin: auto;
  border-radius: 9000px;
`;

/*
 * Component.
 */

export const EntryPointSwitcherItem: FC<EntryPointSwitcherItemProps> = (props) => (
  <DraggableSwitcherItem {...props} renderTooltip={() => props.name}>
    {(isDragging) =>
      props.iconBaseUrl || props.iconUrl ? renderIcon(props, isDragging) : renderColor(props, isDragging)
    }
  </DraggableSwitcherItem>
);

function renderColor(props: EntryPointSwitcherItemProps, isDragging: boolean) {
  return (
    <StyledColorWrapperDiv $isDragging={isDragging} $isSelected={props.isSelected} $color={props.color}>
      <StyledInitialDiv>{props.name[0] || 'P'}</StyledInitialDiv>
    </StyledColorWrapperDiv>
  );
}

function renderIcon(props: EntryPointSwitcherItemProps, isDragging: boolean) {
  if (isDragging) {
    return <img alt="integration-hover" src={buildIconUrl(props, 'hover')} />;
  }

  if (props.iconUrl) {
    return (
      <StyledImageWrapperDiv $isSelected={Boolean(props.isSelected)}>
        <StyledImg alt="integration" src={props.iconUrl} />
      </StyledImageWrapperDiv>
    );
  }

  return (
    <>
      <StyledDefaultIconDiv $isSelected={Boolean(props.isSelected)}>
        <img alt="integration-default" src={buildIconUrl(props, 'hover')} />
      </StyledDefaultIconDiv>
      <StyledHoverIconDiv $isSelected={Boolean(props.isSelected)}>
        <img alt="integration-hover" src={buildIconUrl(props, 'hover')} />
      </StyledHoverIconDiv>
      <StyledSelectedIconDiv $isSelected={Boolean(props.isSelected)}>
        <img alt="integration-selected" src={buildIconUrl(props, 'hover')} />
      </StyledSelectedIconDiv>
    </>
  );
}

function buildIconUrl(props: EntryPointSwitcherItemProps, iconName: string) {
  return `${props.iconBaseUrl}${iconName}.svg`;
}
